"use client";

import { c as _c } from "react/compiler-runtime";
import { Suspense } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { ChevronDown } from "lucide-react";
import { Menu } from "@repo/ui/mantine";
import { Typography } from "./typography";
import styles from "./DiscoveryHeaderNav.module.css";
export type DiscoveryHeaderAuthUser = {
  displayName: string;
};
export type DiscoveryHeaderAuthStudent = {
  id: string;
  username?: string | null;
  displayName?: string | null;
  fullName?: string | null;
};
const EMPTY_STUDENTS: DiscoveryHeaderAuthStudent[] = [];
function studentMenuLabel(student: DiscoveryHeaderAuthStudent) {
  return student.fullName || student.displayName || student.username;
}
function authRedirectHref(path: string, redirect: string) {
  return `${path}?redirect=${encodeURIComponent(redirect)}`;
}
type AuthProps = {
  user: DiscoveryHeaderAuthUser | null;
  students?: DiscoveryHeaderAuthStudent[];
  parentPortalHref: string;
  loginPath: string;
  logoutPath: string;
};
function DiscoveryHeaderAuthLinks(t0) {
  const $ = _c(28);
  const {
    user,
    students: t1,
    parentPortalHref,
    loginPath,
    logoutPath,
    redirectPath
  } = t0;
  const students = t1 === undefined ? EMPTY_STUDENTS : t1;
  if (!user) {
    let t2;
    if ($[0] !== loginPath || $[1] !== redirectPath) {
      t2 = authRedirectHref(loginPath, redirectPath);
      $[0] = loginPath;
      $[1] = redirectPath;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Typography as="span" variant="buttonMd">Sign in</Typography>;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] !== t2) {
      t4 = <a href={t2} className={styles.signIn}>{t3}</a>;
      $[4] = t2;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    return t4;
  }
  const t2 = `Account menu for ${user.displayName}`;
  let t3;
  if ($[6] !== user.displayName) {
    t3 = <Typography as="span" variant="buttonMd" className={styles.accountName}>{user.displayName}</Typography>;
    $[6] = user.displayName;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ChevronDown size={16} strokeWidth={1.5} aria-hidden={true} />;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t3) {
    t5 = <Menu.Target><button type="button" className={styles.accountButton} aria-haspopup="menu" aria-label={t2}>{t3}{t4}</button></Menu.Target>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== students) {
    t6 = students.length > 0 ? <><div className={styles.studentList} role="group" aria-label="Students"><Typography as="p" variant="bodySmall" className={styles.studentListHeading}>Students</Typography><ul className={styles.studentListItems}>{students.map(_temp)}</ul></div><Menu.Divider /></> : null;
    $[12] = students;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== parentPortalHref) {
    t7 = <Menu.Item component="a" href={parentPortalHref} className={styles.accountMenuItem}>Parent Portal</Menu.Item>;
    $[14] = parentPortalHref;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== logoutPath || $[17] !== redirectPath) {
    t8 = authRedirectHref(logoutPath, redirectPath);
    $[16] = logoutPath;
    $[17] = redirectPath;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t8) {
    t9 = <Menu.Item component="a" href={t8} className={styles.accountMenuItem}>Log out</Menu.Item>;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t6 || $[22] !== t7 || $[23] !== t9) {
    t10 = <Menu.Dropdown className={styles.accountMenu}>{t6}{t7}{t9}</Menu.Dropdown>;
    $[21] = t6;
    $[22] = t7;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t5) {
    t11 = <Menu>{t5}{t10}</Menu>;
    $[25] = t10;
    $[26] = t5;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
}
function _temp(student) {
  return <li key={student.id} className={styles.studentListItem}><Typography as="span" variant="buttonMd">{studentMenuLabel(student)}</Typography></li>;
}
function DiscoveryHeaderAuthWithQuery(t0) {
  const $ = _c(8);
  let pathname;
  let props;
  if ($[0] !== t0) {
    ({
      pathname,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = pathname;
    $[2] = props;
  } else {
    pathname = $[1];
    props = $[2];
  }
  const t1 = useSearchParams();
  let t2;
  if ($[3] !== t1) {
    t2 = t1.toString();
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const query = t2;
  const redirectPath = query ? `${pathname}?${query}` : pathname;
  let t3;
  if ($[5] !== props || $[6] !== redirectPath) {
    t3 = <DiscoveryHeaderAuthLinks {...props} redirectPath={redirectPath} />;
    $[5] = props;
    $[6] = redirectPath;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
export function DiscoveryHeaderAuth(props) {
  const $ = _c(3);
  const pathname = usePathname() || "/";
  let t0;
  if ($[0] !== pathname || $[1] !== props) {
    t0 = <Suspense fallback={<DiscoveryHeaderAuthLinks {...props} redirectPath={pathname} />}><DiscoveryHeaderAuthWithQuery {...props} pathname={pathname} /></Suspense>;
    $[0] = pathname;
    $[1] = props;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}